/* eslint-disable @typescript-eslint/member-delimiter-style */
import React, { useEffect, useState } from 'react'
import GetAppIcon from '@material-ui/icons/GetApp'
import { IconButton } from '@material-ui/core'

import moment, { Moment } from 'config/moment'
import { PRIZE } from 'consts'
import { useApiCall } from 'hooks/useApi'

import { useLoadingTracker } from '../../hooks/useLoadingTracker'
import { formatDashboardNumber } from '../../utils/formatDashboardNumber'
import ComparisonTable, { RowData } from '../../components/ComparisonTable'
import { getBenefitsUsed, BenefitsUsedParams, BenefitsUsedResponse, StoreBenefitsData } from './api'
import { REWARD_TYPE_BY_MEMBERSHIP } from '../../../Memberships/Prizes/components/constants'

interface BenefitsUsedProps {
  period: { month: number; year: number }
  startDate?: Moment
  endDate?: Moment
  storeIds?: number[]
  trigger: number
  compara: boolean
  // eslint-disable-next-line react/require-default-props
  onLoaded?: () => void
}

interface FormattedData {
  label: string
  type?: string
  [key: string]: string | number | undefined
}

interface StoreColumn {
  store?: string
  columns: { label: string; key: string }[]
}

const stripHtmlTags = (str: string) => str.replace(/<[^>]*>/g, '')

const getRewardTypeDescription = (type: string, isCampaign: boolean): string => {
  if (type === PRIZE.REWARD_TYPE.VOUCHER) {
    return 'Voucher'
  }
  if (type === 'Cupon' || type === 'C') {
    return 'Cupón'
  }
  if (isCampaign) {
    if (type === 'O') {
      return 'Campaña – Recurrente'
    }
    if (type === 'M') {
      return 'Campaña – Uso único'
    }
  }
  const rewardType = REWARD_TYPE_BY_MEMBERSHIP.find(value => value.id === type)
  return rewardType?.description || type
}

const BenefitsUsed: React.FC<BenefitsUsedProps> = ({
  period,
  startDate,
  endDate,
  storeIds,
  trigger,
  compara,
  onLoaded,
}) => {
  const [data, setData] = useState<FormattedData[]>([])
  const [stores, setStores] = useState<StoreColumn[]>([])
  const [error, setError] = useState<string | null>(null)
  const [apiData, setApiData] = useState<StoreBenefitsData[]>([])
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  })

  const [statsCall, statsLoading] = useApiCall<BenefitsUsedParams, BenefitsUsedResponse>(
    getBenefitsUsed,
  )

  const [rowsPerPage, setRowsPerPage] = useState(5)
  const isLoading = statsLoading || isLoadingData

  // Track loading state
  useLoadingTracker({ isLoading, componentId: 'BenefitsUsed' })

  const fetchData = async (page = 1, pageSize?: number) => {
    try {
      setIsLoadingData(true)
      setError(null)

      // Use provided startDate and endDate if available, otherwise fallback to period calculation
      let startDateStr: string
      let endDateStr: string

      if (startDate && endDate) {
        // Use the provided dates directly
        startDateStr = startDate.format('YYYY-MM-DD')
        endDateStr = endDate.format('YYYY-MM-DD')
      } else {
        // Fallback to period-based calculation (for backward compatibility)
        const now = moment()
        const periodDate = moment()
          .year(period.year)
          .month(period.month)
        const isCurrentMonth =
          periodDate.year() === now.year() && periodDate.month() === now.month()

        startDateStr = periodDate
          .clone()
          .startOf('month')
          .format('YYYY-MM-DD')
        // Si es el mes actual, usar hoy como fecha final, sino usar el último día del mes
        endDateStr = isCurrentMonth
          ? now.format('YYYY-MM-DD')
          : periodDate
              .clone()
              .endOf('month')
              .format('YYYY-MM-DD')
      }

      const response = await statsCall({
        startDate: startDateStr,
        endDate: endDateStr,
        // No pasar storeIds cuando están seleccionados todos los locales (undefined)
        // Esto hace que el backend devuelva datos agregados
        ...(storeIds && storeIds.length > 0 ? { storeIds } : {}),
        page,
        limit: pageSize ?? rowsPerPage,
      })

      if (response?.data) {
        if (Array.isArray(response.data)) {
          // Si es un array pero no tiene storeId, es porque son datos agregados
          // Convertirlo a un solo objeto con datos agregados
          if (response.data.length > 0 && !response.data.some(d => d.storeId)) {
            setApiData(response.data)
          } else {
            setApiData(response.data)
          }
        } else {
          setApiData([response.data as StoreBenefitsData])
        }
      }
    } catch (apiError) {
      console.error('Error fetching data:', apiError)
      setError('Error al cargar datos')
    } finally {
      setIsLoadingData(false)
    }
  }

  const getStoreName = (store: StoreBenefitsData) => {
    if (store?.storeName) {
      return store?.storeName
    }
    // Si no hay storeIds o es undefined, significa que están seleccionados todos los locales
    if (!storeIds || storeIds.length === 0) {
      return 'Todos los locales'
    }
    if (compara && storeIds && storeIds.length > 0) {
      return 'Todos los locales'
    }
    return ''
  }

  const formatDataForTable = () => {
    // Format global data (no stores selected)
    if (!apiData) {
      setStores([])
      setData([])
      return
    }
    // Only one column: Canjes, % sobre registros
    const storesConfig: StoreColumn[] = apiData.map(store => ({
      store: getStoreName(store),
      columns: [
        { label: 'Canjes', key: 'canjes' },
        { label: '% sobre registros', key: 'percent' },
      ],
    }))

    // Build a list of unique benefit IDs across stores to avoid collapsing by title
    const idToTitle = new Map<number, string>()
    const idToType = new Map<number, string>()
    const idToIsCampaign = new Map<number, number>()
    apiData.forEach(store => {
      store.benefits.forEach(benefit => {
        if (!idToTitle.has(benefit.id)) {
          idToTitle.set(benefit.id, benefit.title)
          idToType.set(benefit.id, benefit.type)
          idToIsCampaign.set(benefit.id, benefit.isCampaign || 0)
        }
      })
    })

    const formattedData: FormattedData[] = Array.from(idToTitle.entries()).map(
      ([benefitId, title]) => {
        const rawType = idToType.get(benefitId) || ''
        const isCampaign = idToIsCampaign.get(benefitId) || 0
        const rowData: FormattedData = {
          label: stripHtmlTags(title),
          type: getRewardTypeDescription(rawType, isCampaign === 1),
        }

        apiData.forEach(store => {
          const benefit = store.benefits.find(b => b.id === benefitId)
          if (benefit) {
            rowData[`${getStoreName(store)}_canjes`] = formatDashboardNumber(benefit.exchanges)
            rowData[`${getStoreName(store)}_percent`] = `${benefit.percentage.toFixed(2)}%`
          } else {
            rowData[`${getStoreName(store)}_canjes`] = '0'
            rowData[`${getStoreName(store)}_percent`] = '0.00%'
          }
        })

        return rowData
      },
    )

    setStores(storesConfig)
    setData(formattedData)
    setError(null)
  }

  const fetchAllData = async (page = 1, pageSize?: number) => {
    setError(null)
    setApiData([])

    // Single call with all storeIds (or none for global data)
    await fetchData(page, pageSize)
  }

  const setPaginationData = () => {
    if (apiData && apiData.length > 0) {
      // Encuentra el store con mayor totalPages
      const { currentPage, totalPages, hasNextPage, hasPrevPage } = apiData.reduce((max, curr) =>
        curr.totalPages > max.totalPages ? curr : max,
      )
      setPagination({
        currentPage,
        totalPages,
        hasNextPage,
        hasPrevPage,
      })
    }
  }

  const onPageChange = (page: number) => {
    setPagination({ ...pagination, currentPage: page })
    fetchAllData(page)
  }

  const onRowsPerPageChange = (value: number) => {
    setRowsPerPage(value)
    setPagination(prev => ({ ...prev, currentPage: 1 }))
    fetchAllData(1, value)
  }

  const downloadBenefitsCSV = async () => {
    try {
      setIsLoadingData(true)

      // Obtener todas las fechas
      let startDateStr: string
      let endDateStr: string

      if (startDate && endDate) {
        startDateStr = startDate.format('YYYY-MM-DD')
        endDateStr = endDate.format('YYYY-MM-DD')
      } else {
        const now = moment()
        const periodDate = moment()
          .year(period.year)
          .month(period.month)
        const isCurrentMonth =
          periodDate.year() === now.year() && periodDate.month() === now.month()

        startDateStr = periodDate
          .clone()
          .startOf('month')
          .format('YYYY-MM-DD')
        endDateStr = isCurrentMonth
          ? now.format('YYYY-MM-DD')
          : periodDate
              .clone()
              .endOf('month')
              .format('YYYY-MM-DD')
      }

      // Hacer una llamada para obtener todos los datos sin paginación
      const response = await statsCall({
        startDate: startDateStr,
        endDate: endDateStr,
        ...(storeIds && storeIds.length > 0 ? { storeIds } : {}),
        page: 1,
        limit: 10000, // Número grande para obtener todos los datos
      })

      if (!response?.data) {
        return
      }

      // Procesar los datos de la misma manera que formatDataForTable
      let allStoreData: StoreBenefitsData[] = []
      if (Array.isArray(response.data)) {
        if (response.data.length > 0 && !response.data.some(d => d.storeId)) {
          allStoreData = response.data
        } else {
          allStoreData = response.data
        }
      } else {
        allStoreData = [response.data as StoreBenefitsData]
      }

      // Formatear los datos para el CSV
      const allStoresConfig: StoreColumn[] = allStoreData.map(store => ({
        store: getStoreName(store),
        columns: [
          { label: 'Canjes', key: 'canjes' },
          { label: '% sobre registros', key: 'percent' },
        ],
      }))

      // Construir el mapa de beneficios
      const idToTitle = new Map<number, string>()
      const idToType = new Map<number, string>()
      const idToIsCampaign = new Map<number, number>()
      allStoreData.forEach(store => {
        store.benefits.forEach(benefit => {
          if (!idToTitle.has(benefit.id)) {
            idToTitle.set(benefit.id, benefit.title)
            idToType.set(benefit.id, benefit.type)
            idToIsCampaign.set(benefit.id, benefit.isCampaign || 0)
          }
        })
      })

      const allFormattedData: FormattedData[] = Array.from(idToTitle.entries()).map(
        ([benefitId, title]) => {
          const rawType = idToType.get(benefitId) || ''
          const isCampaign = idToIsCampaign.get(benefitId) || 0
          const rowData: FormattedData = {
            label: stripHtmlTags(title),
            type: getRewardTypeDescription(rawType, isCampaign === 1),
          }

          allStoreData.forEach(store => {
            const benefit = store.benefits.find(b => b.id === benefitId)
            if (benefit) {
              rowData[`${getStoreName(store)}_canjes`] = formatDashboardNumber(benefit.exchanges)
              rowData[`${getStoreName(store)}_percent`] = `${benefit.percentage.toFixed(2)}%`
            } else {
              rowData[`${getStoreName(store)}_canjes`] = '0'
              rowData[`${getStoreName(store)}_percent`] = '0.00%'
            }
          })

          return rowData
        },
      )

      // Generar el CSV
      const rows: string[][] = []
      // Header
      const headerRow = ['Beneficio', 'Tipo de Beneficio']
      allStoresConfig.forEach(store =>
        store.columns.forEach(col => {
          headerRow.push(store.store ? `${store.store} - ${col.label}` : col.label)
        }),
      )
      rows.push(headerRow)
      // Data
      allFormattedData.forEach(row => {
        const rowArr = [row.label, row.type || '']
        allStoresConfig.forEach(store =>
          store.columns.forEach(col => {
            rowArr.push(row[`${store.store}_${col.key}`]?.toString() ?? '')
          }),
        )
        rows.push(rowArr)
      })
      const csvContent = rows.map(e => e.join(',')).join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'beneficios_usados.csv'
      a.click()
      URL.revokeObjectURL(url)
    } catch (downloadError) {
      console.error('Error downloading CSV:', downloadError)
      setError('Error al descargar el archivo CSV')
    } finally {
      setIsLoadingData(false)
    }
  }

  useEffect(() => {
    if (trigger > 0) {
      const run = async () => {
        try {
          await fetchAllData()
        } finally {
          if (onLoaded) onLoaded()
        }
      }
      run()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger])

  useEffect(() => {
    formatDataForTable()
    setPaginationData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiData])

  return (
    <div className="space-y-4">
      <ComparisonTable
        title="Conversión - Uso de beneficios"
        header={
          apiData && apiData.length > 0 && apiData.some(d => d.storeId)
            ? 'Beneficio/Tienda'
            : 'Beneficio'
        }
        stores={stores}
        data={data as RowData[]}
        globalColumn={{ label: 'Tipo de Beneficio', key: 'type' }}
        downloadButton={
          <IconButton
            disabled={isLoading}
            className="ml-2"
            aria-label="download"
            onClick={downloadBenefitsCSV}
          >
            <GetAppIcon />
          </IconButton>
        }
        isLoading={isLoading}
        error={error}
        emptyMessage="No hay datos disponibles para mostrar en este momento."
        {...pagination}
        totalCount={
          apiData && apiData.length > 0
            ? apiData.reduce(
                (max, curr) => (curr.totalItems.length > max ? curr.totalItems.length : max),
                0,
              )
            : undefined
        }
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </div>
  )
}

BenefitsUsed.defaultProps = {
  startDate: undefined,
  endDate: undefined,
  storeIds: [],
}

export default BenefitsUsed
